import { Head, Link } from "@inertiajs/react"
import { Check, Link2, Megaphone, Shield, Trophy } from "lucide-react"
import { motion, useInView } from "motion/react"
import { useRef, useState } from "react"

import { Heading } from "@/components/heading"
import { PageHeader } from "@/components/page-header"
import { RaceShield } from "@/components/race-shield"
import { ResultsTicker } from "@/components/results-ticker"
import { ShieldFrame } from "@/components/shield-frame"
import { TeamDashboard } from "@/components/team-dashboard"
import { TeamName } from "@/components/team-name"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { formatGold, positionClass } from "@/lib/format"
import { getRaceDisplayName, getTeamInitials } from "@/lib/race-colors"
import { cn } from "@/lib/utils"
import { ScheduleCarousel } from "@/pages/leagues/schedule-carousel"
import { leaguePath, leagueTeamPath, newLeagueTeamPath } from "@/routes"
import type { BreadcrumbItem, LeagueHubProps } from "@/types"

import { useClipboard } from "./use-clipboard"

/* ─────────────────────────────────────────────────────────
 * STANDINGS ANIMATION STORYBOARD
 *
 * Read top-to-bottom. Each `at` value is ms after scroll into view.
 *
 *    0ms   waiting for standings card to enter viewport
 *  150ms   first row slides up + fades in
 *   …      each subsequent row staggered by 60ms
 * ───────────────────────────────────────────────────────── */

const STANDINGS = {
  firstRow: 0.15, // seconds — delay before first row appears
  stagger: 0.06, // seconds between each row
  offsetY: 16, // px each row slides up from
  spring: { type: "spring" as const, stiffness: 300, damping: 30 },
}

function seasonDescription(
  season: LeagueHubProps["season"],
  coachCount: number,
): string {
  const coachLabel = coachCount === 1 ? "1 coach" : `${coachCount} coaches`
  if (!season) return `No season yet · ${coachLabel}`
  const statusLabel =
    season.status === "draft"
      ? "Pre-Season Draft"
      : season.status === "active"
        ? "Season Underway"
        : "Season Complete"
  return `Season ${season.number} · ${statusLabel} · ${coachLabel}`
}

function TeamBanner({
  leagueId,
  teamId,
  rank,
  teamName,
  race,
  colorHue,
  coachName,
  teamValue,
  wins,
  draws,
  losses,
  points,
  needsAttention,
  isUserTeam,
}: {
  leagueId: string
  teamId: string
  rank: number
  teamName: string
  race: string
  colorHue: number | null
  coachName: string
  teamValue: number
  wins: number
  draws: number
  losses: number
  points: number
  needsAttention?: boolean
  isUserTeam?: boolean
}) {
  return (
    <Link
      href={leagueTeamPath(leagueId, teamId)}
      className="group/row hover:bg-surface-hover flex items-start gap-4 rounded-lg px-2 py-3 transition-colors duration-150"
    >
      <span
        className={cn(
          "font-display w-6 flex-shrink-0 pt-1 text-right text-xl font-semibold",
          positionClass(rank),
        )}
      >
        {rank}
      </span>
      <RaceShield
        race={race}
        hue={colorHue ?? undefined}
        label={getTeamInitials(teamName)}
        size="md"
      />
      <div className="flex min-w-0 flex-1 flex-col" style={{ minHeight: 78 }}>
        <div>
          <span className="inline-flex items-center gap-1.5 font-semibold">
            <TeamName name={teamName} hue={colorHue} isUser={isUserTeam} />
            {needsAttention && (
              <Megaphone
                className="size-3.5 flex-shrink-0 animate-pulse"
                style={{
                  color: "var(--neon-gold)",
                  filter: "drop-shadow(0 0 4px oklch(0.85 0.28 85 / 0.5))",
                }}
              />
            )}
          </span>
          <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            {getRaceDisplayName(race)}
          </p>
          <p className="text-muted-foreground mt-0.5 text-xs">
            Coached by{" "}
            <span className="text-foreground/80 font-medium">{coachName}</span>
          </p>
        </div>
        <div className="mt-auto flex items-baseline gap-2">
          <span className="font-display text-accent text-sm leading-none font-bold">
            {formatGold(teamValue)}
          </span>
          <span className="text-muted-foreground text-xs">TV</span>
        </div>
      </div>
      <div className="flex flex-shrink-0 flex-col items-end gap-1 pt-1">
        <span
          className={cn(
            "font-display text-2xl font-bold",
            points > 0 && "text-[var(--accent)]",
          )}
        >
          {points}
        </span>
        <span className="text-muted-foreground text-xs">
          {wins}W {draws}D {losses}L
        </span>
      </div>
    </Link>
  )
}

function StandingsList({
  leagueId,
  standings,
  userTeamId,
}: {
  leagueId: string
  standings: LeagueHubProps["standings"]
  userTeamId?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-80px" })

  return (
    <div ref={ref} className="space-y-0">
      {standings.map((row, i) => (
        <motion.div
          key={row.team.id}
          initial={{ opacity: 0, y: STANDINGS.offsetY }}
          animate={
            isInView
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: STANDINGS.offsetY }
          }
          transition={{
            ...STANDINGS.spring,
            delay: STANDINGS.firstRow + i * STANDINGS.stagger,
          }}
        >
          {i > 0 && <div className="border-border mx-2 border-b" />}
          <TeamBanner
            leagueId={leagueId}
            teamId={row.team.id}
            rank={row.rank}
            teamName={row.team.name}
            race={row.team.race}
            colorHue={row.team.color_hue}
            coachName={row.team.coach_name}
            teamValue={row.team.team_value}
            wins={row.wins}
            draws={row.draws}
            losses={row.losses}
            points={row.points}
            isUserTeam={userTeamId === row.team.id}
          />
        </motion.div>
      ))}
    </div>
  )
}

export default function LeagueHub({
  league,
  season,
  coach_count,
  user_team,
  invite_url,
  recent_results,
  standings,
  upcoming_match,
  roster_alerts,
  rounds,
  user_team_id,
}: LeagueHubProps) {
  const [, copy] = useClipboard()
  const [showCopied, setShowCopied] = useState(false)

  const handleCopyInvite = () => {
    void copy(invite_url).then((ok) => {
      if (ok) {
        setShowCopied(true)
        setTimeout(() => setShowCopied(false), 2000)
      }
    })
  }

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: league.name,
      href: leaguePath(league.id),
      leagueSwitcher: true,
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={league.name} />

      <div className="mx-auto w-full max-w-7xl px-4 pt-8">
        <PageHeader
          title={league.name}
          description={seasonDescription(season, coach_count)}
          actions={
            <Button variant="secondary" size="sm" onClick={handleCopyInvite}>
              {showCopied ? (
                <>
                  <Check className="text-accent mr-2 size-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Link2 className="text-accent-dim mr-2 size-4" />
                  Invite Coaches
                </>
              )}
            </Button>
          }
        />

        {/* Hero card — Build Your Team CTA or Team Dashboard */}
        <div>
          {!user_team ? (
            <Link
              href={newLeagueTeamPath(league.id)}
              className="group/cta block"
            >
              <Card
                className="animate-race-card-breathe border-cta-border py-0 transition-shadow group-hover/cta:shadow-[0_0_32px_oklch(0.8_0.16_85/0.25)]"
                style={{
                  background:
                    "linear-gradient(180deg, oklch(0.16 0.01 85 / 0.4), var(--surface-hover))",
                }}
              >
                <CardContent className="flex flex-col items-center justify-center gap-4 px-6 py-12 text-center">
                  <ShieldFrame
                    size={64}
                    gradient="linear-gradient(180deg, oklch(0.30 0.10 85), oklch(0.18 0.05 85))"
                  >
                    <Shield
                      className="size-8"
                      style={{ color: "var(--accent)" }}
                    />
                  </ShieldFrame>
                  <p
                    className="font-display text-4xl leading-none font-bold"
                    style={{
                      color: "var(--accent)",
                      textShadow:
                        "0 0 6px oklch(0.8 0.16 85 / 0.3), 0 0 16px oklch(0.7 0.14 85 / 0.12), 0 0 24px oklch(0.8 0.3 145 / 0.2), 0 1px 0 oklch(0.55 0.1 85), 0 2px 4px oklch(0 0 0 / 0.5)",
                    }}
                  >
                    Build Your Team
                  </p>
                  <p className="text-muted-foreground max-w-sm text-sm">
                    Draft your roster and join the fray. Blood Bowl awaits.
                  </p>
                  <Button className="mt-2">
                    <Shield className="mr-2 size-4" />
                    Draft Team
                  </Button>
                </CardContent>
              </Card>
            </Link>
          ) : (
            <TeamDashboard
              leagueId={league.id}
              userTeam={user_team}
              upcomingMatch={upcoming_match}
              rosterAlerts={roster_alerts}
              standings={standings}
            />
          )}
        </div>
      </div>

      {/* Schedule Carousel — broadcast band */}
      {rounds.length > 0 && (
        <div
          className="relative mt-10 mb-10 w-full py-8"
          style={{
            background:
              "linear-gradient(180deg, var(--band-edge), var(--surface-sunken) 30%, var(--surface-sunken) 70%, var(--band-edge))",
          }}
        >
          <div className="mx-auto mb-4 flex max-w-7xl items-center justify-between px-5">
            <Heading level={2} wrapperClassName="mb-0">
              Schedule
            </Heading>
            <span className="font-display text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Season {season?.number ?? 1}
            </span>
          </div>
          <div className="overflow-hidden px-4">
            <ScheduleCarousel
              rounds={rounds}
              userTeamId={user_team_id}
              leagueId={league.id}
            />
          </div>
        </div>
      )}

      <div
        className={cn(
          "mx-auto w-full max-w-7xl px-4",
          rounds.length === 0 && "pt-10",
          recent_results.length > 0 && "pb-24",
        )}
      >
        <div className="mb-10">
          <Card className="py-0 transition-shadow hover:shadow-[0_4px_16px_black/60]">
            <CardHeader className="px-6 pt-5 pb-2">
              <CardTitle>Standings</CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {standings.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-6 text-center">
                  <ShieldFrame
                    size={56}
                    gradient="linear-gradient(180deg, var(--surface-raised), var(--card))"
                  >
                    <Trophy
                      className="size-7"
                      style={{ color: "var(--accent-dim)" }}
                    />
                  </ShieldFrame>
                  <p className="text-muted-foreground text-sm">
                    The League Table Stands Empty
                  </p>
                </div>
              ) : (
                <StandingsList
                  leagueId={league.id}
                  standings={standings}
                  userTeamId={user_team?.id}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {recent_results.length > 0 && (
        <ResultsTicker leagueId={league.id} results={recent_results} />
      )}
    </AppLayout>
  )
}
